import Image from 'next/image';
import type { Dispatch, Ref, SetStateAction } from 'react';
import { forwardRef, memo } from 'react';

import { akamaiLoader } from '@/utils/akamaiLoader';

import type { NextImageProps } from '@/components/NextImage/NextImage';
import { captureMessage } from '@/lib/errors';

import { getLoaderWidth } from './helper';

export interface NextImageMemoProps extends NextImageProps {
  setState: Dispatch<SetStateAction<'loading' | 'loaded' | 'error'>>;
}
export const NextImageMemo = memo(
  forwardRef(function ImageMemo(
    { alt, className, setState, ...props }: NextImageMemoProps,
    ref: Ref<HTMLImageElement>, // state,
  ) {
    const { aspectRatioHeight, aspectRatioWidth, focalPoint, ...rest } = props;

    return (
      <Image
        ref={ref}
        alt={alt || 'NextImage'}
        className={`next-image ${className || ''}`}
        data-test-id='NextImageMemoImage'
        loader={({ src, width }) => {
          // the deviceSizes in next.config have larger size(like 3840, 2048),
          // when the width big than origin image width, set width = origin image width
          return akamaiLoader({
            src,
            width: getLoaderWidth(width, rest.originalImageDimensions),
            requestWidth: props.width ? +props.width : undefined,
            aspectRatioHeight,
            aspectRatioWidth,
            focalPoint,
          });
        }}
        onError={() => {
          setState('error');
          captureMessage('Failed to load image', {
            tags: {
              image: true,
            },
            extra: {
              src: props.src,
            },
          });
        }}
        onLoad={() => setState('loaded')}
        {...rest}
      />
    );
  }),
  (prevProps, nextProps) => {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps);
  },
);

NextImageMemo.displayName = 'NextImageMemo';
